import { useEffect, useState, useCallback } from 'react'
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import api from '../../utils/apiClient'
import useGlobalTheme from '../../hooks/useGlobalTheme'

const USER_TYPES = ['free', 'pro', 'dev']
const TRIAL_PRESETS = [1, 3, 7, 30]
const TRIAL_MAX_DAYS = 365

function formatDate(value) {
	if (!value) return '—'
	try {
		return new Date(value).toLocaleString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
			second: '2-digit',
			hour12: true,
		})
	} catch {
		return '—'
	}
}

function Section({ title, children, action, isLight }) {
	return (
		<div className="mb-6">
			<div className="flex items-center justify-between mb-2">
				<h3 className={`text-xs font-semibold uppercase tracking-wide ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>
					{title}
				</h3>
				{action}
			</div>
			<div className={`rounded-lg ring-1 p-3 ${
				isLight ? 'bg-gray-50 ring-gray-200' : 'bg-[#15202b] ring-white/5'
			}`}>
				{children}
			</div>
		</div>
	)
}

function Row({ label, value, isLight }) {
	return (
		<div className="flex items-start justify-between py-1 text-sm">
			<span className={isLight ? 'text-gray-500' : 'text-gray-500'}>{label}</span>
			<span className={`text-right break-all max-w-[60%] ${isLight ? 'text-gray-900' : 'text-gray-200'}`}>
				{value}
			</span>
		</div>
	)
}

function AdminUserDetail({ userId, onClose, onChanged }) {
	const [user, setUser] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [busyAction, setBusyAction] = useState(null)
	const [feedback, setFeedback] = useState(null)
	const [customDays, setCustomDays] = useState('')
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const load = useCallback(async () => {
		setLoading(true)
		setError(null)
		try {
			const { data } = await api.get(`/api/admin/users/${userId}`)
			setUser(data)
		} catch (err) {
			console.error('[admin] failed to load user:', err)
			setError(err.response?.data?.message || 'Failed to load user')
		} finally {
			setLoading(false)
		}
	}, [userId])

	useEffect(() => {
		load()
	}, [load])

	const runAction = async (key, fn, successMsg) => {
		setBusyAction(key)
		setFeedback(null)
		try {
			await fn()
			setFeedback({ kind: 'success', text: successMsg })
			await load()
			onChanged?.()
		} catch (err) {
			console.error(`[admin] action ${key} failed:`, err)
			setFeedback({
				kind: 'error',
				text: err.response?.data?.message || 'Action failed',
			})
		} finally {
			setBusyAction(null)
		}
	}

	const handleUserTypeChange = newType => {
		if (newType === user.userType) return
		if (!window.confirm(`Change ${user.email} to ${newType}?`)) return
		runAction(
			'userType',
			() => api.patch(`/api/admin/users/${userId}`, { userType: newType }),
			'User type updated',
		)
	}

	const handleLogoutDevice = (deviceId, label) => {
		if (
			!window.confirm(`Force-logout ${label || 'this device'} for ${user.email}?`)
		)
			return
		runAction(
			`logoutDevice-${deviceId}`,
			() =>
				api.post(`/api/admin/users/${userId}/terminate-session`, { deviceId }),
			'Session terminated',
		)
	}

	const handleResyncSubscription = () => {
		runAction(
			'resync',
			() => api.post(`/api/admin/users/${userId}/resync-subscription`),
			'Subscription resynced',
		)
	}

	const applyTrial = days => {
		if (!Number.isInteger(days) || days < 1 || days > TRIAL_MAX_DAYS) return
		if (
			!window.confirm(
				`Apply ${days}-day trial to ${user.email}? This will replace any in-progress trial.`,
			)
		)
			return
		runAction(
			`applyTrial-${days}`,
			() => api.post(`/api/admin/users/${userId}/apply-trial`, { days }),
			`${days}-day trial applied`,
		)
	}

	const handleApplyCustomTrial = e => {
		e.preventDefault()
		const days = Number(customDays)
		if (!Number.isInteger(days) || days < 1 || days > TRIAL_MAX_DAYS) {
			setFeedback({
				kind: 'error',
				text: `Enter a whole number of days between 1 and ${TRIAL_MAX_DAYS}`,
			})
			return
		}
		applyTrial(days)
		setCustomDays('')
	}

	return (
		<div
			className="fixed inset-0 z-40 flex justify-end bg-black/50"
			onClick={onClose}
		>
			<div
				className={`w-full max-w-xl h-full ring-1 overflow-y-auto ${
					isLight ? 'bg-white ring-gray-200' : 'bg-[#1a2634] ring-white/10'
				}`}
				onClick={e => e.stopPropagation()}
			>
				<div className={`sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b ${
					isLight ? 'bg-white border-gray-200' : 'bg-[#1a2634] border-white/5'
				}`}>
					<div>
						<h2 className={`text-base font-semibold ${isLight ? 'text-gray-900' : 'text-white'}`}>User detail</h2>
						<p className={`text-xs mt-0.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>{user?.email || ''}</p>
					</div>
					<button
						onClick={onClose}
						className={`p-1 rounded ${
							isLight
								? 'hover:bg-gray-50 text-gray-500 hover:text-gray-900'
								: 'hover:bg-white/5 text-gray-400 hover:text-gray-200'
						}`}
					>
						<XMarkIcon className="h-5 w-5" />
					</button>
				</div>

				<div className="p-5">
					{loading ? (
						<div className={`text-sm ${isLight ? 'text-gray-500' : 'text-gray-400'}`}>Loading…</div>
					) : error ? (
						<div className={`px-3 py-2 rounded-lg bg-red-500/10 ring-1 ring-red-500/30 text-sm ${isLight ? 'text-red-600' : 'text-red-300'}`}>
							{error}
						</div>
					) : user ? (
						<>
							{feedback && (
								<div
									className={`mb-4 px-3 py-2 rounded-lg text-sm ring-1 ${
										feedback.kind === 'success'
											? isLight
												? 'bg-emerald-500/10 ring-emerald-500/30 text-emerald-700'
												: 'bg-emerald-500/10 ring-emerald-500/30 text-emerald-300'
											: isLight
												? 'bg-red-500/10 ring-red-500/30 text-red-600'
												: 'bg-red-500/10 ring-red-500/30 text-red-300'
									}`}
								>
									{feedback.text}
								</div>
							)}

							<Section title="Identity" isLight={isLight}>
								<Row label="Email" value={user.email} isLight={isLight} />
								<Row label="User ID" value={String(user._id)} isLight={isLight} />
								<Row
									label="Email verified"
									value={user.isEmailVerified ? 'Yes' : 'No'}
									isLight={isLight}
								/>
								<Row
									label="2FA enabled"
									value={user.twoFactorEnabled ? 'Yes' : 'No'}
									isLight={isLight}
								/>
								<Row label="Created" value={formatDate(user.createdAt)} isLight={isLight} />
							</Section>

							<Section
								title="Subscription"
								isLight={isLight}
								action={
									<button
										onClick={handleResyncSubscription}
										disabled={busyAction === 'resync'}
										className="text-xs flex items-center gap-1 text-sky-300 hover:text-sky-200 disabled:opacity-50"
									>
										<ArrowPathIcon
											className={`h-3.5 w-3.5 ${
												busyAction === 'resync' ? 'animate-spin' : ''
											}`}
										/>
										Resync
									</button>
								}
							>
								<Row label="Type" value={user.subscription?.type || 'free'} isLight={isLight} />
								<Row
									label="Active"
									value={user.subscription?.isActive ? 'Yes' : 'No'}
									isLight={isLight}
								/>
								<Row
									label="Trial ends"
									value={formatDate(user.subscription?.trialEndsAt)}
									isLight={isLight}
								/>
								<Row
									label="Stripe customer"
									value={user.stripeCustomerId || '—'}
									isLight={isLight}
								/>

								<div className={`mt-3 pt-3 border-t ${isLight ? 'border-gray-200' : 'border-white/5'}`}>
									<div className={`text-xs mb-2 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>Apply trial</div>
									<div className="flex flex-wrap items-center gap-1.5">
										{TRIAL_PRESETS.map(d => {
											const key = `applyTrial-${d}`
											return (
												<button
													key={d}
													onClick={() => applyTrial(d)}
													disabled={busyAction === key}
													className={`px-2.5 py-1 rounded text-xs ring-1 disabled:opacity-50 ${
														isLight
															? 'bg-gray-50 text-gray-900 ring-gray-200 hover:bg-gray-100'
															: 'bg-white/5 text-gray-200 ring-white/10 hover:bg-white/10'
													}`}
												>
													{busyAction === key ? '…' : `${d} day${d === 1 ? '' : 's'}`}
												</button>
											)
										})}
										<form
											onSubmit={handleApplyCustomTrial}
											className="flex items-center gap-1 ml-auto"
										>
											<input
												type="number"
												min="1"
												max={TRIAL_MAX_DAYS}
												value={customDays}
												onChange={e => setCustomDays(e.target.value)}
												placeholder="Custom"
												className={`w-20 text-xs rounded ring-1 px-2 py-1 focus:outline-none focus:ring-sky-500/40 ${
													isLight
														? 'bg-white ring-gray-300 text-gray-900 placeholder-gray-400'
														: 'bg-[#0f1620] ring-white/10 text-gray-200 placeholder-gray-500'
												}`}
											/>
											<button
												type="submit"
												disabled={
													!customDays ||
													(busyAction || '').startsWith('applyTrial-')
												}
												className="px-2.5 py-1 rounded text-xs ring-1 bg-sky-500/15 text-sky-300 ring-sky-500/30 hover:bg-sky-500/25 disabled:opacity-50"
											>
												Apply
											</button>
										</form>
									</div>
								</div>
							</Section>

							<Section title="Role" isLight={isLight}>
								<div className="flex items-center justify-between py-1 text-sm">
									<span className={isLight ? 'text-gray-500' : 'text-gray-500'}>User type</span>
									<div className="flex gap-1">
										{USER_TYPES.map(t => (
											<button
												key={t}
												onClick={() => handleUserTypeChange(t)}
												disabled={busyAction === 'userType'}
												className={`px-2 py-0.5 rounded text-xs ring-1 disabled:opacity-50 ${
													user.userType === t
														? 'bg-sky-500/15 text-sky-300 ring-sky-500/30'
														: isLight
															? 'bg-gray-50 text-gray-700 ring-gray-200 hover:bg-gray-100'
															: 'bg-white/5 text-gray-300 ring-white/10 hover:bg-white/10'
												}`}
											>
												{t}
											</button>
										))}
									</div>
								</div>
							</Section>

							<Section
								title={`Active sessions (${user.devices?.length || 0})`}
								isLight={isLight}
							>
								{(user.devices || []).length === 0 ? (
									<div className={`text-xs py-1 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>No active sessions</div>
								) : (
									user.devices.map(d => {
										const deviceLabel = `${d.browser || 'Unknown'} on ${d.os || 'Unknown'}`
										const busyKey = `logoutDevice-${d.deviceId}`
										return (
											<div
												key={d.deviceId}
												className={`py-1.5 border-b last:border-0 text-sm ${isLight ? 'border-gray-200' : 'border-white/5'}`}
											>
												<div className="flex items-start justify-between gap-3">
													<div className="min-w-0 flex-1">
														<div className="flex items-center gap-2 flex-wrap">
															<span className={isLight ? 'text-gray-900' : 'text-gray-200'}>{deviceLabel}</span>
															<span className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
																{d.deviceType}
															</span>
														</div>
														<div className={`text-xs mt-0.5 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
															{d.city || 'Unknown'}, {d.country || '—'} · last
															seen {formatDate(d.lastCheckIn)}
														</div>
													</div>
													<button
														onClick={() => handleLogoutDevice(d.deviceId, deviceLabel)}
														disabled={busyAction === busyKey}
														className={`shrink-0 px-2 py-0.5 rounded text-xs ring-1 disabled:opacity-50 ${
															isLight
																? 'bg-gray-50 text-red-600 ring-gray-200 hover:bg-red-500/10 hover:ring-red-500/30'
																: 'bg-white/5 text-red-300 ring-white/10 hover:bg-red-500/10 hover:ring-red-500/30'
														}`}
													>
														{busyAction === busyKey ? '…' : 'Logout'}
													</button>
												</div>
											</div>
										)
									})
								)}
							</Section>

							<Section
								title={`Email accounts (${user.emailAccounts?.length || 0})`}
								isLight={isLight}
							>
								{(user.emailAccounts || []).length === 0 ? (
									<div className={`text-xs py-1 ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
										None connected
									</div>
								) : (
									user.emailAccounts.map(acc => (
										<div
											key={acc._id || acc.email}
											className={`py-1.5 border-b last:border-0 text-sm flex justify-between ${isLight ? 'border-gray-200' : 'border-white/5'}`}
										>
											<span className={isLight ? 'text-gray-900' : 'text-gray-200'}>{acc.email}</span>
											<span className={`text-xs ${isLight ? 'text-gray-500' : 'text-gray-500'}`}>
												{acc.provider || 'unknown'}
											</span>
										</div>
									))
								)}
							</Section>
						</>
					) : null}
				</div>
			</div>
		</div>
	)
}

export default AdminUserDetail
